import { useAuth } from "@/context/AuthContext";
import { CreatorPromptCard } from "./CreatorPromptCard";
import { PromptFeedSkeleton } from "../Home/PromptFeedSkeleton";
import { EmptyPromptFeed } from "../Home/EmptyPromptFeed";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useInfinitePrompts } from "@/hooks/prompts/use-infinite-prompts";
import { InfiniteScrollTrigger } from "../Home/PromptFeed/InfiniteScrollTrigger";
import { useEffect, useState } from "react";
import { Category, SortOption, Prompt, GeoFilters } from "@/lib/types";
import { motion } from "framer-motion";
import { PromptDetailModal } from "@/components/Prompts/PromptDetailModal";
import { usePromptDetail } from "@/hooks/use-prompt-detail";

interface ModernPromptFeedProps {
  selectedCategory?: Category;
  selectedSort?: SortOption;
  searchQuery?: string;
  showSaved?: boolean;
  showMyPrompts?: boolean;
  filterUserId?: string;
  geoFilters?: GeoFilters;
}

export function ModernPromptFeed({ 
  selectedCategory,
  selectedSort,
  searchQuery, 
  showSaved = false,
  showMyPrompts = false,
  filterUserId,
  geoFilters: externalGeoFilters
}: ModernPromptFeedProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedPrompt, isOpen, openPromptDetail, closePromptDetail, updateSelectedPrompt } = usePromptDetail();
  
  const { 
    prompts, 
    loading,
    loadingMore,
    hasMore,
    loadMore,
    updatePrompt,
    deletePrompt,
    setCategory,
    setSort,
    setGeoFilters,
    category,
    sort,
    geoFilters
  } = useInfinitePrompts(user?.id);

  // Sync external category prop with internal state
  useEffect(() => {
    if (selectedCategory && selectedCategory !== category) {
      setCategory(selectedCategory);
    }
  }, [selectedCategory, category, setCategory]);

  // Sync external sort prop with internal state
  useEffect(() => {
    if (selectedSort && selectedSort !== sort) {
      setSort(selectedSort);
    }
  }, [selectedSort, sort, setSort]);

  // Sync external geoFilters prop with internal state
  useEffect(() => {
    if (externalGeoFilters && JSON.stringify(externalGeoFilters) !== JSON.stringify(geoFilters)) {
      setGeoFilters(externalGeoFilters);
    }
  }, [externalGeoFilters, geoFilters, setGeoFilters]);

  const handleNewPost = () => {
    if (!user) {
      toast.error("You need to sign in to create a new prompt");
      navigate("/auth");
    } else {
      navigate("/create-prompt");
    }
  };

  const handlePromptClick = (prompt: Prompt) => {
    openPromptDetail(prompt);
  };

  const handlePromptUpdate = (id: string, updates: Partial<Prompt>) => {
    updatePrompt(id, updates);
    if (selectedPrompt?.id === id) {
      updateSelectedPrompt(updates);
    }
  };

  if (loading) {
    return <PromptFeedSkeleton />;
  }

  return (
    <section id="prompts-section">
      {/* Feed Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex items-center justify-between mb-8"
      >
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-1 text-white">
            Trending Prompts
          </h2>
          <p className="text-white/50 text-sm">
            Discover the most popular prompts from our community
          </p>
        </div>
        <Button
          onClick={handleNewPost}
          className="flex items-center gap-2 bg-gradient-to-r from-brand-purple to-brand-cyan hover:shadow-[0_0_20px_rgba(112,0,255,0.4)] text-white font-semibold px-5 py-2.5 rounded-xl transition-all duration-300"
        >
          <PlusCircle className="h-4 w-4" />
          <span className="hidden sm:inline">New Prompt</span>
        </Button>
      </motion.div>

      {/* 2-Column Grid */}
      {prompts.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {prompts.map((prompt, index) => (
              <motion.div
                key={prompt.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(index, 8) * 0.05 }}
                data-prompt-id={prompt.id}
              >
                <CreatorPromptCard
                  prompt={prompt}
                  onUpdate={handlePromptUpdate}
                  onDelete={deletePrompt}
                  onClick={() => handlePromptClick(prompt)}
                />
              </motion.div>
            ))}
          </div>
          <InfiniteScrollTrigger
            onLoadMore={loadMore}
            loading={loadingMore}
            hasMore={hasMore}
          />
        </>
      ) : (
        <EmptyPromptFeed />
      )}

      {/* Prompt Detail Modal */}
      <PromptDetailModal
        prompt={selectedPrompt}
        isOpen={isOpen}
        onClose={closePromptDetail}
        onUpdate={handlePromptUpdate}
      />
    </section>
  );
}
